import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CAPABILITY_LABELS, INTEGRATION_STATUS_LABELS, PLAN_LABELS, type IntegrationKey, type IntegrationStatus, type IntegrationSummary } from '@kontor/shared';
import { api } from '../api';
import { useSession } from '../session';
import { ChatEmbedPage } from './ChatEmbedPage';
import { WebsiteFormsPage } from './WebsiteFormsPage';
import { EmailIntegration } from './integrations/EmailIntegration';
import { PaypalIntegration, StripeIntegration } from './integrations/PaymentIntegrations';
import { SocialIntegration } from './integrations/SocialIntegration';
import './integrations.css';

const tone = (s: IntegrationStatus) => (s === 'connected' ? 'badge--won' : s === 'error' ? 'badge--rejected' : '');

/** Zeichen für die Karten, schlicht und ohne Marken: ein Buchstabe oder Symbol je Dienst */
const MARK: Record<IntegrationKey, string> = { email: '@', stripe: 'S', paypal: 'P', facebook: 'f', linkedin: 'in', tiktok: '♪' };

/**
 * Anbindungen der eigenen Webseite (Schlüssel legt der Mandant selbst an): das Anfrageformular, der Chat und die Newsletter-Anmeldung. Sie gehören zum jeweiligen Modul. Ein Modul, das das Paket
 * nicht enthält und das der Betreiber nicht freigeschaltet hat, zeigt die Kachel gar nicht erst.
 */
const WEBSITE_TILES = [
  { key: 'anfragen', module: 'inquiries', mark: '?', label: 'Anfragen (Webseite)', description: 'Das Anfrageformular Ihrer Webseite sendet seine Anfragen direkt an Wirkia. Sie legen den Schlüssel dafür selbst an.', caps: ['Schlüssel für Ihre Webseite', 'Anfragetypen mit Feldern', 'Beispielaufruf'] },
  { key: 'chat', module: 'webchat', mark: '…', label: 'Chat (Webseite)', description: 'Das Chat-Fenster von Wirkia auf Ihrer eigenen Webseite. Schlüssel anlegen, Code kopieren oder das Plugin herunterladen.', caps: ['Schlüssel für Ihre Webseite', 'Plugin für WordPress', 'Code zum Einbauen'] },
  { key: 'newsletter', module: 'marketing', mark: 'N', label: 'Newsletter (Webseite)', description: 'Das Anmeldeformular für Ihren Newsletter sendet die Anmeldung direkt an Wirkia. Sie legen den Schlüssel dafür selbst an.', caps: ['Schlüssel für Ihre Webseite', 'Anmeldung mit Bestätigungsmail', 'Beispielaufruf'] },
] as const;

/** Einrichtung → APIs und Webhooks (KON-186): Übersicht aller Verbindungen. Ein Dienst, den das Paket nicht enthält, ist sichtbar, aber gesperrt. */
export function IntegrationsPage() {
  const { key } = useParams();
  const { hasModule } = useSession();
  const tiles = WEBSITE_TILES.filter((t) => hasModule(t.module));
  const list = useQuery({ queryKey: ['integrations'], queryFn: async () => (await api<{ data: IntegrationSummary[] }>('GET', '/integrations')).data });
  if (list.isLoading) return <p className="muted">Wird geladen …</p>;
  if (!list.data) return <div className="alert alert--error" role="alert">Die Verbindungen konnten nicht geladen werden.</div>;
  const open = key ? list.data.find((i) => i.key === key) : undefined;
  const website = key ? tiles.find((t) => t.key === key) : undefined;

  if (website) {
    return (
      <>
        <p><Link to="/einrichtung/api" className="back-link">← Alle Verbindungen</Link></p>
        <div className="page-head">
          <h1>{website.label}</h1>
        </div>
        {website.key === 'anfragen' && <WebsiteFormsPage kind="inquiries" />}
        {website.key === 'newsletter' && <WebsiteFormsPage kind="newsletter" />}
        {website.key === 'chat' && <ChatEmbedPage />}
      </>
    );
  }

  if (open) {
    return (
      <>
        <p><Link to="/einrichtung/api" className="back-link">← Alle Verbindungen</Link></p>
        <div className="page-head">
          <h1>{open.label}</h1>
          <span className={`badge ${tone(open.status)}`}>{INTEGRATION_STATUS_LABELS[open.status]}</span>
        </div>
        <p className="muted">{open.description}</p>
        {!open.available ? (
          <div className="alert alert--warn" role="status">Diese Verbindung ist ab dem Paket {PLAN_LABELS[open.plan]} enthalten. Sprechen Sie uns an, wenn Sie sie freischalten möchten.</div>
        ) : !open.configured ? (
          <div className="alert alert--warn" role="status">Diese Verbindung ist auf dem Server noch nicht eingerichtet. Bitte wenden Sie sich an den Support.</div>
        ) : open.key === 'email' ? (
          <EmailIntegration />
        ) : open.key === 'stripe' ? (
          <StripeIntegration />
        ) : open.key === 'paypal' ? (
          <PaypalIntegration />
        ) : open.key === 'facebook' || open.key === 'linkedin' || open.key === 'tiktok' ? (
          <SocialIntegration provider={open.key} />
        ) : (
          <div className="card"><p className="muted">Die Einrichtung dieser Verbindung ist in Vorbereitung.</p></div>
        )}
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>APIs und Webhooks</h1>
      </div>
      <p className="muted">Verbindungen von Wirkia zu Diensten außerhalb: Ihr E-Mail-Postfach, Zahlungsdienste und soziale Netzwerke, dazu Anfrageformular, Chat und Newsletter-Anmeldung Ihrer eigenen Webseite, soweit Ihr Paket sie enthält. Zugangsdaten liegen verschlüsselt und werden nach dem Speichern nie wieder angezeigt.</p>
      <div className="integration-grid">
        {list.data.map((i) => (
          <article key={i.key} className={`card integration${i.available ? '' : ' integration--off'}`}>
            <header className="integration__head">
              <span className="integration__mark" aria-hidden="true">{MARK[i.key]}</span>
              <h2>{i.label}</h2>
              <span className={`badge ${tone(i.status)}`}>{INTEGRATION_STATUS_LABELS[i.status]}</span>
            </header>
            <p className="small">{i.description}</p>
            {i.summary && <p className="small"><strong>{i.summary}</strong></p>}
            <ul className="integration__caps small muted">
              {i.capabilities.map((c) => <li key={c}>{CAPABILITY_LABELS[c]}</li>)}
            </ul>
            {i.lastError && i.status === 'error' && <p className="small integration__error" role="alert">{i.lastError}</p>}
            <footer className="integration__foot">
              {i.available ? (
                <Link to={`/einrichtung/api/${i.key}`} className={`btn ${i.status === 'not_connected' ? 'btn--primary' : 'btn--secondary'}`}>{i.status === 'not_connected' ? 'Einrichten' : 'Bearbeiten'}</Link>
              ) : (
                <span className="muted small">Ab Paket {PLAN_LABELS[i.plan]}</span>
              )}
            </footer>
          </article>
        ))}
        {tiles.map((t) => (
          <article key={t.key} className="card integration">
            <header className="integration__head">
              <span className="integration__mark" aria-hidden="true">{t.mark}</span>
              <h2>{t.label}</h2>
            </header>
            <p className="small">{t.description}</p>
            <ul className="integration__caps small muted">
              {t.caps.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <footer className="integration__foot">
              <Link to={`/einrichtung/api/${t.key}`} className="btn btn--secondary">Öffnen</Link>
            </footer>
          </article>
        ))}
      </div>
    </>
  );
}
